import React, { useEffect, useRef, useState } from "react";
import DashboardLayout from "../../../templates/DashboardLayout";
import FileUploadZone from "../components/FileUploadZone";
import MessageBubble, { StreamingBubble } from "../components/MessageBubble";
import { useDocumentChat } from "../hooks/useDocumentChat";

const DocumentChatPage: React.FC = () => {
  const { state, uploadFile, sendMessage, resetSession } = useDocumentChat();
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef    = useRef<HTMLTextAreaElement>(null);

  const isStreaming = state.stage === "streaming";
  const hasSession  = !!state.sessionId;

  // Auto-scroll on new messages / streaming
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [state.messages, state.streamingContent]);

  // Auto-grow textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = "auto";
      ta.style.height = Math.min(ta.scrollHeight, 140) + "px";
    }
  }, [input]);

  const handleSend = () => {
    const msg = input.trim();
    if (!msg || isStreaming) return;
    setInput("");
    sendMessage(msg);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col h-full bg-gray-50">

        {!hasSession ? (
          // ── Upload state ───────────────────────────────────────────────────
          <>
            {state.stage === "error" && state.error && (
              <div className="mx-auto mt-4 max-w-xl px-4">
                <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3
                  flex items-start gap-2 text-sm text-red-700">
                  <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  {state.error}
                </div>
              </div>
            )}
            <FileUploadZone
              onFile={uploadFile}
              uploading={state.stage === "uploading"}
            />
          </>
        ) : (
          // ── Chat state ─────────────────────────────────────────────────────
          <div className="flex flex-col h-full">

            {/* Header */}
            <div className="shrink-0 bg-white border-b border-gray-100 shadow-sm px-6 py-3
              flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg,#7C3AED,#2563EB)" }}>
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 leading-tight truncate max-w-xs">
                    {state.filename}
                  </p>
                  <p className="text-xs text-gray-400">
                    {state.wordCount.toLocaleString()} words
                    {state.pageCount > 0 ? ` · ${state.pageCount} pages` : ""}
                    {state.isChunked ? " · using relevant excerpts" : " · full document in context"}
                  </p>
                </div>
              </div>
              <button
                onClick={resetSession}
                className="flex items-center gap-1.5 text-xs font-medium text-gray-500
                  hover:text-gray-800 border border-gray-200 hover:border-gray-300
                  rounded-lg px-3 py-1.5 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 4v16m8-8H4"/>
                </svg>
                New document
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
              {state.messages.length === 0 && !isStreaming && (
                <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
                  <div className="text-4xl">💬</div>
                  <div>
                    <p className="text-gray-700 font-medium">Document ready</p>
                    <p className="text-gray-400 text-sm mt-0.5">
                      Ask anything about <span className="font-medium text-gray-600">{state.filename}</span>
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-center mt-2 max-w-sm">
                    {[
                      "Summarise this document",
                      "What are the key points?",
                      "What action is required?",
                    ].map(q => (
                      <button
                        key={q}
                        onClick={() => { setInput(q); textareaRef.current?.focus(); }}
                        className="text-xs px-3 py-1.5 rounded-full border border-purple-200
                          bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {state.messages.map((msg, i) => (
                <MessageBubble key={i} message={msg} />
              ))}

              {isStreaming && (
                <StreamingBubble content={state.streamingContent} />
              )}

              {state.stage === "error" && state.error && (
                <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3
                  text-sm text-red-700 flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  {state.error}
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input bar */}
            <div className="shrink-0 bg-white border-t border-gray-100 px-6 py-4">
              <div className="flex items-end gap-3 bg-gray-50 border border-gray-200
                rounded-2xl px-4 py-3 focus-within:border-purple-400
                focus-within:ring-2 focus-within:ring-purple-100 transition-all">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask anything about this document…"
                  rows={1}
                  disabled={isStreaming}
                  className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400
                    resize-none outline-none leading-relaxed disabled:opacity-50"
                  style={{ maxHeight: "140px" }}
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isStreaming}
                  className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center
                    transition-all duration-150
                    disabled:opacity-40 disabled:cursor-not-allowed
                    enabled:hover:scale-105 enabled:active:scale-95"
                  style={{ background: "linear-gradient(135deg,#7C3AED,#2563EB)" }}
                >
                  {isStreaming ? (
                    <svg className="w-4 h-4 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                    </svg>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1.5 text-center">
                Press Enter to send · Shift+Enter for new line
              </p>
            </div>

          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DocumentChatPage;
